import { Component, inject, ViewChild } from '@angular/core';
import { TableConfig } from '@shared/interfaces/table/table-config';
import { TableComponent } from '@shared/components/table/table.component';
import {
  AuthRoles,
  AverageQueryMeteredDataResultDto,
  DataHubStatus,
  EstimationMethod,
  MeteredDataSource,
  MeteringPointDetails,
  MeteringPointsService,
  MeterStandType,
  ObservationTimeSeriesPeriod,
} from '@app/api/dmc';
import { HeaderService } from '@app/core/services/header.service';
import { AlertService } from '@app/core/services/alert.service';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { meteringPointTimeseriesTable } from '@shared/table-configs/meteringpoint/meteringpoint-timeseries.table';
import {
  getMeteringPointTimeSeriesActionMenu
} from '@views/meteringpoint/meteringpoint-timeseries/meteringpoint-timeseries.action-menu';
import { ModalService } from '@app/core/services/modal.service';
import {
  RequestTimeseriesModalComponent
} from '@shared/modals/timeseries/request-timeseries-modal/request-timeseries-modal.component';
import {
  SubmitTimeseriesModalComponent
} from '@shared/modals/timeseries/submit-timeseries-modal/submit-timeseries-modal.component';
import {
  AdvancedSubmitTimeseriesModalComponent
} from '@shared/modals/timeseries/advanced-submit-timeseries-modal/advanced-submit-timeseries-modal.component';
import { CardComponent } from '@shared/components/card/card.component';
import { InputGroupComponent } from '@shared/components/input/input-group/input-group.component';
import { LabelDirective } from '@shared/components/input/label/label.directive';
import { InputDirective } from '@shared/components/input/input/input.directive';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { enumToKeyValue } from '@app/core/utils/enum-to-key-value';
import { TranslatePipe } from '@ngx-translate/core';
import { SelectDirective } from '@shared/components/input/select/select.directive';
import { ButtonDirective } from '@shared/components/input/button/button.directive';
import { KeyValue } from '@shared/interfaces/key-value/key-value';
import { InputDateDirective } from '@shared/components/input/date/input-date.directive';
import { ODataService } from '@app/core/services/odata/odata.service';
import { ODataType } from '@app/core/services/odata/types/odata-type';
import { DateCellComponent } from '@shared/components/table/cells/date-cell/date-cell.component';
import { DATE_FORMAT } from '@app/core/constants/date-format';
import { DATE_TIME_FORMAT } from '@app/core/constants/date-time-format';
import { SortParam } from '@app/core/constants/sort-param';
import {
  UploadManualTimeseriesModalComponent
} from '@shared/modals/timeseries/upload-manual-estimates-modal/upload-manual-timeseries-modal.component';
import { UserService } from '@app/core/services/user.service';
import { AppConfigService } from '@root/app/core/config/app-config.service';
import { TimeseriesGraphComponent } from '@shared/components/timeseries-graph/timeseries-graph.component';
import { TimeseriesGraphConfig } from '@shared/components/timeseries-graph/timeseries-graph-config';
import { TimeSeriesData } from '@shared/components/timeseries-graph/timeseries-data.interface';
import { lastValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
  selector: 'app-meteringpoint-timeseries',
  imports: [
    TableComponent,
    CardComponent,
    InputGroupComponent,
    LabelDirective,
    InputDirective,
    ReactiveFormsModule,
    TranslatePipe,
    SelectDirective,
    ButtonDirective,
    InputDateDirective,
    TimeseriesGraphComponent,
  ],
  templateUrl: './meteringpoint-timeseries.component.html',
  styleUrl: './meteringpoint-timeseries.component.css'
})
export class MeteringpointTimeseriesComponent {
  private readonly appConfig: AppConfigService = inject(AppConfigService);
  private readonly userService = inject(UserService);

  @ViewChild('table', { static: false }) public table?: TableComponent<any>;
  @ViewChild('graph', { static: false }) public graphComponent?: TimeseriesGraphComponent;

  public queryForm: FormGroup = new FormGroup({
    meteringPointIdentification: new FormControl(null, [Validators.required]),
    start: new FormControl(null, [Validators.required]),
    resolution: new FormControl(ObservationTimeSeriesPeriod.Day, [Validators.required]),
    dataHubStatus: new FormControl(null),
    source: new FormControl(null),
    onlyLatest: new FormControl('no'),
    consumptionType: new FormControl(null),
    estimationMethod: new FormControl(null),
    packageId: new FormControl(''),
  });

  /** Dropdown values **/
  public timeseriesResolution: KeyValue[] = enumToKeyValue(ObservationTimeSeriesPeriod, {
    omitKeys: ['NotSpecified', 'Other'],
    prefixValue: 'resolution.'
  });
  public dataHubStatus: KeyValue[] = enumToKeyValue(DataHubStatus, {
    omitKeys: ['NotSpecified'],
    prefixValue: 'status.'
  });
  public source: KeyValue[] = enumToKeyValue(MeteredDataSource, {
    omitKeys: ['NotSpecified'],
    prefixValue: 'enum.metereddatasource.'
  });
  public onlyLatest: KeyValue[] = [{ key: 'yes', value: 'common.yes' }, { key: 'no', value: 'common.no' }];
  public consumptionType: KeyValue[] = enumToKeyValue(MeterStandType, {
    omitKeys: ['NotSpecified', 'Estimated', 'NotApplicable'],
    prefixValue: 'enum.readingtype.'
  });
  public estimationMethod: KeyValue[] = enumToKeyValue(EstimationMethod, {
    omitKeys: this.estimationMethodOmitKeys(),
    prefixValue: 'enum.estimationmethod.'
  });

  /** Table Config **/
  public timeSeriesTable: TableConfig<AverageQueryMeteredDataResultDto> = {
    ...meteringPointTimeseriesTable,
    serviceCallback: (pageSize: number, page: number, order?: SortParam) => this.search(pageSize, page, order),
    refreshable: true,
  };

  /** ID **/
  public id?: string;

  public meteringPoint?: MeteringPointDetails;

  /** Graph **/
  public showGraph: boolean = false;
  public graphConfig: TimeseriesGraphConfig = {
    id: 'meteringpoint-timeseries-graph',
    dateRange: false,
    defaultVisualization: 'bar',
    availableVisualizations: ['bar', 'line'],
    showLegend: true,
    reset: true,
    callback: () => this.fetchGraphData(),
  };

  constructor(
    private readonly route: ActivatedRoute,
    private readonly headerMenuService: HeaderService,
    private readonly modalService: ModalService,
    private readonly oDataService: ODataService,
    private readonly alertService: AlertService,
    private readonly meteringPointsService: MeteringPointsService,
  ) {

    this.route.parent?.paramMap.subscribe((q: ParamMap) => {
      this.id = q.get('id')!;
      this.meteringPointsService.meteringPointsIdentificationGet(this.id).subscribe((meteringPoint: MeteringPointDetails) => {
        this.meteringPoint = meteringPoint
      });
      this.queryForm.get('meteringPointIdentification')!.setValue(this.id);
    });

    /** Enable / Disable form controls based on resolution **/
    this.queryForm.get('resolution')?.valueChanges.subscribe((resolution?: ObservationTimeSeriesPeriod | null) => {
      if (resolution === ObservationTimeSeriesPeriod.Hour || resolution === ObservationTimeSeriesPeriod.Quarter) {
        this.queryForm.get('dataHubStatus')?.enable();
        this.queryForm.get('source')?.enable();
        this.queryForm.get('onlyLatest')?.enable();
        this.queryForm.get('consumptionType')?.enable();
        this.queryForm.get('estimationMethod')?.enable();
        this.queryForm.get('packageId')?.enable();

        if (resolution === ObservationTimeSeriesPeriod.Hour) {
          this.queryForm.get('onlyLatest')?.setValue('yes');
        }
      } else {
        this.queryForm.get('onlyLatest')?.setValue(null);

        this.queryForm.get('dataHubStatus')?.disable();
        this.queryForm.get('source')?.disable();
        this.queryForm.get('onlyLatest')?.disable();
        this.queryForm.get('consumptionType')?.disable();
        this.queryForm.get('estimationMethod')?.disable();
        this.queryForm.get('packageId')?.disable();
      }
    });

    this.registerHeaderActions();
    this.queryForm.valueChanges.subscribe(() => this.alertService.clearAll());
  }

  public onSearch(): void {
    if (this.queryForm.invalid) return;
    this.alertService.clearAll();

    const resolution = this.queryForm.get('resolution')?.value;

    let dateFormat: string;
    if (resolution === ObservationTimeSeriesPeriod.Month) {
      dateFormat = 'MM-yyyy';
    } else if (resolution === ObservationTimeSeriesPeriod.Year) {
      dateFormat = 'yyyy';
    } else if (resolution === ObservationTimeSeriesPeriod.Day) {
      dateFormat = DATE_FORMAT;
    } else {
      dateFormat = DATE_TIME_FORMAT;
    }

    this.swapDateFormat(dateFormat);
    this.graphConfig = { ...this.graphConfig, dateFormat };

    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    this.table?.search();

    if (this.showGraph) {
      this.graphComponent?.fetchData();
    }
  }

  public toggleGraph(): void {
    this.showGraph = !this.showGraph;
    this.registerHeaderActions();
    if (this.showGraph) {
      // Wait one tick for @if to render the graph component into the DOM
      setTimeout(() => this.graphComponent?.fetchData());
    }
  }

  private registerHeaderActions(): void {
    const mlEnabled = this.appConfig.settings.featureFlags.machineLearningEstimation.enabled;
    const roles = this.userService.user()?.roles ?? [];
    const mpWriter = roles.includes(AuthRoles.MeteringPointWriter);

    const callbackMap: Record<string, () => void> = {
      request: () => this.openRequestModal(),
      manual: () => this.openSubmitModal(),
      upload: () => this.openUploadModal(),
      toggleGraph: () => this.toggleGraph(),
    };

    const disabledMap: Record<string, () => boolean> = {
      request: () => !mpWriter,
      manual: () => !mpWriter,
      upload: () => !mpWriter,
    };

    if (mlEnabled) {
      callbackMap['advanced'] = () => this.openAdvancedSubmitModal();
      disabledMap['advanced'] = () => !mpWriter;
    }

    this.headerMenuService.setActions(
      getMeteringPointTimeSeriesActionMenu(mlEnabled, this.showGraph),
      callbackMap,
      disabledMap,
    );
  }

  private fetchGraphData(): Promise<TimeSeriesData[]> {
    const data = this.queryForm.value;

    if (!data.meteringPointIdentification || !data.start || !data.resolution) {
      return Promise.resolve([]);
    }

    const isDateTimeResolution =
      data.resolution === ObservationTimeSeriesPeriod.Hour ||
      data.resolution === ObservationTimeSeriesPeriod.Quarter;
    const startParam = isDateTimeResolution ? `${data.start}T00:00:00Z` : data.start;

    // OData is hard-limited to 100 entities
    const query = this.oDataService.dmc.query()
      .paginate(1, 100)
      .param('meteringpointIdentification', data.meteringPointIdentification)
      .param('Start', startParam)
      .param('Resolution', data.resolution);

    if (data.dataHubStatus && data.dataHubStatus !== DataHubStatus.NotSpecified) query.param('DataHubStatus', data.dataHubStatus);
    if (data.source && data.source !== MeteredDataSource.NotSpecified) query.param('Source', data.source);
    if (data.consumptionType && data.consumptionType !== MeterStandType.NotSpecified) query.param('consumptionType', data.consumptionType);
    if (data.estimationMethod && data.estimationMethod !== EstimationMethod.NotSpecified) query.param('EstimationMethod', data.estimationMethod);
    if (data.packageId) query.param('PackageId', data.packageId);
    if (data.onlyLatest === 'yes') query.param('OnlyLatest', 'true');

    query.orderBy('readingTs asc');

    return lastValueFrom(
      this.oDataService.dmc.getCollection<AverageQueryMeteredDataResultDto>(
        ODataType.queryMeteredData,
        query,
      ).pipe(
        map((response: any) => {
          const items: AverageQueryMeteredDataResultDto[] = response?.value ?? response ?? [];
          const timeSeriesData: TimeSeriesData[] = [];

          for (const item of items) {
            if (!item.readingTs) continue;

            if (item.consumption != null) {
              timeSeriesData.push({
                id: 'Consumption',
                data: { timestamp: item.readingTs, value: item.consumption },
              });
            }

            if (item.readingValue != null) {
              timeSeriesData.push({
                id: 'ReadingValue',
                data: { timestamp: item.readingTs, value: item.readingValue },
              });
            }
          }

          return timeSeriesData;
        })
      )
    );
  }

  public openRequestModal(): void {
    this.modalService.show({
      component: RequestTimeseriesModalComponent,
      title: 'meteringpoint.timeseries.request.title',
      actions: [
        { type: 'primary', label: 'system.button.save', method: 'submit' },
        { type: 'secondary', label: 'system.button.cancel', closeOnClick: true },
      ],
      params: { id: this.id },
    });
  }

  public openUploadModal(): void {
    this.modalService.show({
      component: UploadManualTimeseriesModalComponent,
      title: 'meteringpoint.csvupload.title',
      actions: [
        { type: 'primary', label: 'meteringpoint.csvupload.upload', method: 'submit' },
        { type: 'secondary', label: 'system.button.cancel', closeOnClick: true },
      ],
      params: { id: this.id }
    });
  }

  public openSubmitModal(): void {
    this.modalService.show({
      component: SubmitTimeseriesModalComponent,
      title: 'meteringpoint.timeseries.submit.title',
      size: 'xl',
      actions: [
        { type: 'primary', label: 'system.button.save', method: 'submit' },
        { type: 'secondary', label: 'system.button.cancel', closeOnClick: true },
      ],
      params: {
        id: this.id,
        resolution: this.meteringPoint?.detailCharacteristics.meterReadingOccurrence
      },
    });
  }

  public openAdvancedSubmitModal(): void {
    this.modalService.show({
      component: AdvancedSubmitTimeseriesModalComponent,
      title: 'meteringpoint.timeseries.advancedsubmit.title',
      size: 'xl',
      actions: [
        { type: 'primary', label: 'system.button.save', method: 'submit' },
        { type: 'secondary', label: 'system.button.cancel', closeOnClick: true },
      ],
      params: {
        id: this.id,
        resolution: this.meteringPoint?.detailCharacteristics.meterReadingOccurrence
      },
    });
  }

  public search(pageSize: number, page: number, sort?: SortParam) {
    const data = this.queryForm.value;

    // Required params are set directly on the query builder.
    // Optional params are only appended when the control has a value
    // (disabled controls surface as null in .value, so they are naturally skipped).
    const isDateTimeResolution = data.resolution === ObservationTimeSeriesPeriod.Hour
      || data.resolution === ObservationTimeSeriesPeriod.Quarter;
    const startParam = isDateTimeResolution ? `${data.start}T00:00:00Z` : data.start;

    const query = this.oDataService.dmc.query()
      .paginate(page, pageSize)
      .count()
      .param('meteringpointIdentification', data.meteringPointIdentification)
      .param('Start', startParam)
      .param('Resolution', data.resolution);

    if (data.dataHubStatus && data.dataHubStatus !== DataHubStatus.NotSpecified) query.param('DataHubStatus', data.dataHubStatus);
    if (data.source && data.source !== MeteredDataSource.NotSpecified) query.param('Source', data.source);
    if (data.consumptionType && data.consumptionType !== MeterStandType.NotSpecified) query.param('consumptionType', data.consumptionType);
    if (data.estimationMethod && data.estimationMethod !== EstimationMethod.NotSpecified) query.param('EstimationMethod', data.estimationMethod);
    if (data.packageId) query.param('PackageId', data.packageId);
    if (data.onlyLatest === 'yes') query.param('OnlyLatest', 'true');

    // Apply user-selected sort; fall back to most-recent-first when none is set.
    if (sort) {
      query.orderBy(`${sort.sortField} ${sort.sortDirection}`);
    } else {
      query.orderBy('readingTs desc');
    }

    return this.oDataService.dmc.getCollection<AverageQueryMeteredDataResultDto>(
      ODataType.queryMeteredData,
      query,
    );
  }

  public swapDateFormat(format: string): void {
    const updatedColumns = (this.timeSeriesTable.columns ?? []).map(col => {
      if (col.cellRenderer === DateCellComponent && col.key === 'readingTs') {
        return {
          ...col,
          cellRendererParams: {
            ...(col.cellRendererParams),
            format: format ?? DATE_FORMAT,
          },
        };
      }
      return col;
    });

    this.timeSeriesTable = {
      ...this.timeSeriesTable,
      columns: updatedColumns,
    } as TableConfig<AverageQueryMeteredDataResultDto>;
  }

  private estimationMethodOmitKeys(): EstimationMethod[] {
    let estimationMethodValues: EstimationMethod[] = [
      EstimationMethod.NotSpecified,
      EstimationMethod.ManualNotSpecified
    ];

    if (this.appConfig.settings.featureFlags.machineLearningEstimation.enabled === false) {
      estimationMethodValues.push(
        EstimationMethod.AdvancedVEE,
        EstimationMethod.ManualAdvancedVEE,
        EstimationMethod.AdvancedVEELookBack,
        EstimationMethod.AdvancedVEEClustering,
        EstimationMethod.AdvancedVEEEVHandler
      );
    }

    return estimationMethodValues;
  }
}
